import React from 'react';
import { Button, Stack, Text, Box, Inline, Alert, TextLink } from '@origin-digital/ods-core';
import { IconHome } from '@origin-digital/ods-icons';

type AddressMeterSearchProps<T extends { addressLabel?: string }> = {
  addressResult: T;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isConfirmEnabled: boolean;
  isLoading: boolean;
  onConfirm: (addressResult: T) => void;
  onSearch: () => void;
  onResetSearch: () => void;
  meterType: 'NMI' | 'MIRN' | 'LOCid/OPCid'; // "LOCid/OPCid" is for Internet address search
  isDedicatedNmiSearch?: boolean;
};

export const AddressMeterSearch = <T extends { addressLabel?: string }>({
  addressResult,
  error,
  isConfirmEnabled,
  isLoading,
  onConfirm,
  onSearch,
  onResetSearch,
  inputRef,
  meterType,
  isDedicatedNmiSearch = false,
}: AddressMeterSearchProps<T>) => {
  if (error) {
    return (
      <Stack space="large">
        <Alert tone="critical">
          {!isDedicatedNmiSearch ? (
            error
          ) : (
            <Text>
              Sorry, we can’t find your address. Please call us on{' '}
              <TextLink href="tel:132461">13 24 61</TextLink>, so we can help you.
            </Text>
          )}
        </Alert>
        <Button
          id="search-error-retry"
          color="primary"
          aria-label="Try again"
          onClick={() => {
            onResetSearch();
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          Try again
        </Button>
      </Stack>
    );
  }

  if (!isConfirmEnabled) {
    return (
      <Button
        id={`${meterType}-search`}
        aria-label={`Search ${meterType}`}
        spinner={isLoading}
        onClick={onSearch}
      >
        Search {meterType}
      </Button>
    );
  }

  return (
    <Box paddingY="medium">
      <Stack space="xlarge">
        <Stack space="small">
          <Text>Is this the right address?</Text>
          <Box borderRadius="medium" border="solid 2px #ffb432" padding="small" alignItems="center">
            <Inline space="small">
              <IconHome />
              <Text variant="body">{addressResult?.addressLabel}</Text>
            </Inline>
          </Box>
        </Stack>
        <Inline space="small">
          <Button
            id={`${meterType}-search-confirm`}
            color="primary"
            aria-label="Yes, this is my address"
            onClick={() => onConfirm(addressResult)}
            disabled={isLoading}
          >
            Yes, this is my address
          </Button>
          <Button
            id={`${meterType}-search-retry`}
            color="primary"
            variant="outlined"
            aria-label="No, try again"
            onClick={() => {
              onResetSearch();
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          >
            No, try again
          </Button>
        </Inline>
      </Stack>
    </Box>
  );
};
